import { Component, Inject, OnInit } from '@angular/core';
import {
    AppNotificationService,
    AppNotificationType,
    BaseCancellationComponent,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigService,
    EscalationService,
    EventService,
    isDefined,
    LocalStorageRecipientsService,
    MaterialService,
    NOTIFICATION_STATUS_CODE,
    NotificationService,
    PaceState,
    StatusModel,
    SynchronizationService,
} from '@evorait/pace-core';
import { cleanupFormList, FormsService } from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { AlertService } from '../../../core/services/eta-alert/alert.service';
import { LocalStorageSalesQuoteService } from '../../../material/services/local-storage-sales-quote.service';
import { OdataCancellationService } from './odata-cancellation.service';

@Component({
    selector: 'pace-core-cancellation',
    templateUrl: './cancellation.component.html',
    styleUrls: ['./cancellation.component.scss'],
})
export class CancellationComponent extends BaseCancellationComponent implements OnInit {
    public modalResult = new Subject<ICancellationResult>();
    public isNotificationInProgressAndCancelled = false;
    public cancellationLabel: string;
    public isLoading = false;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly odataCancellationService: OdataCancellationService,
        private readonly configService: ConfigService,
        private readonly formsService: FormsService,
        private readonly notificationService: NotificationService,
        private readonly appNotificationService: AppNotificationService,
        private readonly synchronizationService: SynchronizationService,
        private readonly escalationService: EscalationService,
        private readonly eventService: EventService,
        private readonly localStorageSalesQuoteService: LocalStorageSalesQuoteService,
        private readonly materialService: MaterialService,
        private readonly store: Store<PaceState>,
        public readonly modalInstance: BsModalRef,
        private readonly alertService: AlertService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly localStorageRecipientsService: LocalStorageRecipientsService,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.isNotificationInProgressAndCancelled = this.notificationService.isNotificationInProgressAndCancelled(
            this.model.notification,
        );
        this.cancellationLabel = this.isNotificationInProgressAndCancelled
            ? <string>this.translate.instant('DASHBOARD_NOTIFICATION_CANCELLATION_IN_PROGRESS_BUTTON_LABEL')
            : <string>this.translate.instant('DASHBOARD_NOTIFICATION_CANCELATION_CONFIRM_CANCELATION_LABEL');
    }

    public sendCancellation(): void {
        this.isLoading = true;
        this.odataCancellationService
            .cancelNotification(this.model)
            .pipe(
                take(1),
                mergeMap(() => this.updateNotificationStatus()),
                tap(() => {
                    // trigger event so that dashboard is refreshed
                    this.eventService.triggerNotificationsUpdated(true);
                    this.isLoading = false;
                    this.modalResult.next({
                        notificationUpdateKey: this.model.notificationUpdateKey,
                    });
                    this.modalResult.complete();
                    this.modalInstance.hide();
                }),
                mergeMap(() => this.cleanupNotificationData()),
                mergeMap(() => {
                    this.log.debug('CancellationComponent: sendCancellation: sendOfflineData');
                    return this.synchronizationService.sendOfflineData();
                }),
                catchError((error) => {
                    this.log.error(`CancellationComponent: sendCancellation: error:`, error);
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('DASHBOARD_NOTIFICATION_CANCELLATION_REQUEST_ERROR_TITLE'),
                        <string>this.translate.instant('DASHBOARD_NOTIFICATION_CANCELLATION_REQUEST_ERROR_BODY'),
                    );
                    this.isLoading = false;
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    public updateNotificationStatus(): Observable<void> {
        if (this.isNotificationInProgressAndCancelled) {
            return this.notificationService.saveCancellationConfirmationForInprogress(
                this.model.notification.identifier,
            );
        } else {
            // set the status to cancelled
            let status =
                this.notificationService.getNotificationStatusByStatusType(
                    this.model.notification,
                    NOTIFICATION_STATUS_CODE.DISP,
                ) ||
                this.notificationService.getNotificationStatusByStatusType(
                    this.model.notification,
                    NOTIFICATION_STATUS_CODE.ETA,
                ) ||
                this.notificationService.getNotificationStatusByStatusType(
                    this.model.notification,
                    NOTIFICATION_STATUS_CODE.CANC,
                );
            if (status) {
                status.statusCode = NOTIFICATION_STATUS_CODE.CNCL;
            } else {
                const now = moment().startOf('minute');
                status = new StatusModel(NOTIFICATION_STATUS_CODE.CNCL, now.clone(), now.clone());
            }
            return this.notificationService.saveStatus(this.model.notification, status);
        }
    }

    /**
     * Once cancellation is confirmed by the user the cancelled notification won't get displayed anymore
     * in the dashboard, and also should not be part of NDP next sync onwards.
     * Hence relevant data should be deleted from the local storage.
     *
     * @returns {Observable<void>} Observable that does the cleanup
     * @throws {Error} if the notification does not have a main equipment
     */
    public cleanupNotificationData(): Observable<void> {
        this.escalationService.removeEscalationTimer(this.model.notification);
        if (this.configService.getDisplayEtaAlert()) {
            this.alertService.removeEtaTimer(this.model.notification);
        }

        let init$ = this.materialService.deleteRequestedMaterialFromLocalStorage(this.model.notification.identifier);

        if (this.clientManager.isNgmP58()) {
            this.localStorageSalesQuoteService.removeSalesQuoteFromLocalStorage(this.model.notification.identifier);

            const mainEquipment = this.model.notification.getMainEquipment();
            if (!mainEquipment) {
                throw new Error(`Notification ${this.model.notification.identifier.toString()} has no main equipment!`);
            }

            init$ = init$.pipe(
                mergeMap(() =>
                    this.localStorageRecipientsService.removeRecipientsFromLocalStorage(
                        this.model.notification.identifier,
                    ),
                ),
                mergeMap(() =>
                    this.formsService
                        .getFormList(
                            mainEquipment.matnr.value,
                            mainEquipment.equnr.value,
                            {
                                frontId: this.model.notification.identifier.frontId,
                                qmNum: this.model.notification.identifier.qmNum,
                                maNum: this.model.notification.identifier.maNum,
                            },
                            true,
                        )
                        .pipe(
                            take(1),
                            filter((formList) => isDefined(formList)),
                            map((formList) => {
                                this.store.dispatch(cleanupFormList({ formList }));
                                return undefined;
                            }),
                            catchError(() => {
                                this.log.debug(
                                    `CancellationComponent: cleanupNotificationData: Form list cleanup not necessary since there is no list`,
                                );
                                return of(undefined);
                            }),
                        ),
                ),
            );
        }

        return init$;
    }
}

export interface ICancellationResult {
    notificationUpdateKey: string;
}
